import { Form, Input, ModalProps, Upload } from 'antd'
import { UploadProps } from 'antd/lib/upload'
import { useWatch } from 'rc-field-form'
import { FunctionComponent, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { nameof } from 'ts-simple-nameof'
import { BasicModal } from '@/components/common/antd/basic-modal'
import UploadFileButton from '@/components/common/buttons/upload-file-button'
import useUploadProps from '@/hooks/antd/use-upload-props'
import { useIntl } from '@/i18n/hooks/use-intl'
import { ModelFileItem } from '@/types/mock-api-types'
import logger from '@/utils/logger'
import { toVoidFunction } from '@/utils/to-void-function'

const REASON_MAXIMUM_LENGTH = 300
const PICTURE_MAXIMUM = 5

interface FeedbackNegativeFormData {
  reason?: string
  uploadInfo?: Parameters<Required<UploadProps<ModelFileItem>>['onChange']>[0]
}

interface FeedbackNegativeModalProps
  extends Pick<ModalProps, 'open' | 'onCancel'> {
  onOk: (values: FeedbackNegativeFormData) => void
  hasUploadInfo?: boolean
}

const FeedbackNegativeModal: FunctionComponent<FeedbackNegativeModalProps> = ({
  open,
  onOk,
  onCancel,
  hasUploadInfo = true,
}) => {
  const { formatMessage } = useIntl()
  const [form] = Form.useForm<FeedbackNegativeFormData>()
  const uploadProps = useUploadProps()
  const containerRef = useRef<HTMLDivElement>(null)

  const reason = useWatch('reason', form)
  const uploadInfo = useWatch('uploadInfo', form)

  /**
   * @description: 不能直接用 Form 的 onFinish 是因為在選圖片的當下 Form 元件會直接 Call 一次 onFinish 造成表單直接被送出
   */
  const handleOk = async (): Promise<void> => {
    try {
      const values = await form.validateFields()
      onOk(values)
    } catch (error) {
      logger.error(error)
    }
  }

  useEffect(() => {
    if (!open) {
      form.resetFields([
        nameof<FeedbackNegativeFormData>((f) => f.reason),
        nameof<FeedbackNegativeFormData>((f) => f.uploadInfo),
      ])
    }
  }, [form, open])

  return (
    <Wrapper
      ref={containerRef}
      onMouseEnter={(e): void => e.stopPropagation()}
      onMouseLeave={(e): void => e.stopPropagation()}
    >
      <BasicModal
        centered
        cancelButtonProps={{ style: { display: 'none' } }}
        getContainer={containerRef?.current ?? document.body}
        maskClosable={false}
        okText={formatMessage({ id: 'general:submit_btn' })}
        open={open}
        title={formatMessage({ id: 'general:feedback_negative_title' })}
        width={520}
        onCancel={onCancel}
        onOk={toVoidFunction(handleOk)}
      >
        <Description>
          {formatMessage({ id: 'general:feedback_negative_description' })}
        </Description>
        <Form form={form} layout='vertical'>
          <ReasonFormItem
            label={formatMessage({ id: 'general:feedback_negative_reason' })}
            name={nameof<FeedbackNegativeFormData>((f) => f.reason)}
            rules={[
              {
                max: REASON_MAXIMUM_LENGTH,
                message: formatMessage(
                  { id: 'error:field_max_length' },
                  {
                    inputField: formatMessage({
                      id: 'general:feedback_negative_reason',
                    }),
                    length: REASON_MAXIMUM_LENGTH,
                  },
                ),
              },
            ]}
          >
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 3 }}
              placeholder={formatMessage({
                id: 'general:feedback_negative_reason_placeholder',
              })}
            />
          </ReasonFormItem>
          <ReasonLength>
            {reason?.length ?? 0}/{REASON_MAXIMUM_LENGTH}
          </ReasonLength>
          {hasUploadInfo && (
            <>
              <ScreenshotsTitle>
                {formatMessage({ id: 'general:feedback_negative_screenshots' })}
              </ScreenshotsTitle>
              <ScreenshotsHint>
                {formatMessage({
                  id: 'general:feedback_negative_screenshots_hint',
                })}
              </ScreenshotsHint>
              <UploadFormItem
                name={nameof<FeedbackNegativeFormData>((f) => f.uploadInfo)}
              >
                <Upload {...uploadProps}>
                  {(uploadInfo?.fileList.length ?? 0) >=
                  PICTURE_MAXIMUM ? null : (
                    <UploadFileButton />
                  )}
                </Upload>
              </UploadFormItem>
            </>
          )}
        </Form>
      </BasicModal>
    </Wrapper>
  )
}

const Wrapper = styled.div``

const Description = styled.div`
  color: ${({ theme }): string => theme.colors.text.primary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
`

const ReasonTitle = styled.div`
  color: ${({ theme }): string => theme.colors.text.secondary};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 40px;
`

const ReasonLength = styled.div`
  color: ${({ theme }): string => theme.colors.base.grey.grey3};
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 140%;
  text-align: end;
`

const ReasonFormItem = styled(Form.Item)`
  margin: 22px 0 0;
`

const ScreenshotsTitle = styled(ReasonTitle)`
  line-height: 140%;
  margin: 0 0 4px;
`

const ScreenshotsHint = styled(ScreenshotsTitle)`
  font-size: 12px;
`

const UploadFormItem = styled(Form.Item)`
  margin: 16px 0 0;
`

export default FeedbackNegativeModal
